import React from 'react';
import { PublicLayout } from '../../components/PublicLayout';
import { PageHero, BenefitsGrid, HowItWorks, CTASection } from '../../components/PageTemplate';
import { 
  Globe, Camera, Video, Layers, Zap, TrendingUp, 
  CheckCircle, Shield, BarChart 
} from 'lucide-react';

export default function LandlordsListing() {
  const steps = [
    {
      title: 'Enter Property Details',
      description: 'Address, type, bedrooms, rent amount, features - our wizard makes it easy.'
    },
    {
      title: 'Upload Media',
      description: 'Photos, floor plans, video tours. We optimize images for each platform.'
    },
    {
      title: 'Complete Compliance',
      description: 'Upload required certificates. We won\'t list until you\'re compliant.'
    },
    {
      title: 'Choose Platforms',
      description: 'Select where to advertise: Rightmove, Zoopla, OnTheMarket, and more.'
    },
    {
      title: 'Review & Publish',
      description: 'Preview your listing, pay any platform fees, and go live instantly.'
    }
  ];

  const benefits = [
    {
      icon: Globe,
      title: 'Major Portal Syndication',
      description: 'One listing goes to Rightmove, Zoopla, OnTheMarket, and our own platform.'
    },
    {
      icon: Camera,
      title: 'Professional Photography',
      description: 'Optional add-on: we send a professional photographer to capture your property.'
    },
    {
      icon: Video,
      title: 'Video & 3D Tours',
      description: 'Upload walkthrough videos or order a 3D virtual tour for better engagement.'
    },
    {
      icon: Layers,
      title: 'Floor Plans',
      description: 'Add professional floor plans to help tenants visualize the space.'
    },
    {
      icon: Shield,
      title: 'Compliance Gating',
      description: 'We won\'t publish until all required certificates are uploaded and valid.'
    },
    {
      icon: TrendingUp,
      title: 'Premium Listings',
      description: 'Boost visibility with featured placement on portals.'
    },
    {
      icon: BarChart,
      title: 'Performance Analytics',
      description: 'Track views, enquiries, and applications for each listing.'
    },
    {
      icon: Zap,
      title: 'Instant Updates',
      description: 'Change price, description, or photos - updates sync to all platforms.'
    }
  ];

  const addOns = [
    { name: 'Professional Photography', price: '£99', description: 'HDR photos by local professionals' },
    { name: 'Video Walkthrough', price: '£149', description: 'Professional video tour' },
    { name: '3D Virtual Tour', price: '£199', description: 'Interactive 360° experience' },
    { name: 'Floor Plan', price: '£49', description: 'Measured floor plan drawing' },
    { name: 'Premium Listing', price: '£25/mo', description: 'Featured placement on portals' },
    { name: 'EPC Certificate', price: '£85', description: 'We arrange the assessment' },
  ];

  return (
    <PublicLayout>
      <PageHero
        badge="LISTING & MARKETING"
        title="Get Your Property Seen"
        subtitle="On Every Major Portal"
        description="List once, publish everywhere. Our platform syndicates your property to Rightmove, Zoopla, and more - with compliance gating built in."
        primaryCTA={{ href: '/register', label: 'List Your Property' }}
        secondaryCTA={{ href: '/landlords/pricing', label: 'View Pricing' }}
        gradient="from-blue-50 via-white to-slate-50"
      />

      <HowItWorks title="Listing Your Property" steps={steps} />

      <BenefitsGrid title="Marketing Features" benefits={benefits} />

      {/* Add-ons Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl font-display font-bold text-center mb-4">
            Optional Add-ons
          </h2>
          <p className="text-center text-slate-600 mb-8 sm:mb-12 max-w-2xl mx-auto">
            Enhance your listing with professional services
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {addOns.map((addon, index) => (
              <div key={index} className="p-5 sm:p-6 border rounded-xl hover:border-primary transition-colors">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-semibold">{addon.name}</h3>
                  <span className="text-primary font-bold">{addon.price}</span>
                </div>
                <p className="text-sm text-slate-600">{addon.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        title="Ready to list your property?"
        description="Create an account and list your first property in under 10 minutes."
        primaryCTA={{ href: '/register', label: 'Get Started' }}
      />
    </PublicLayout>
  );
}
